import {DateTime} from 'luxon';
import * as React from 'react';
import {FilterDropdown} from 'src/components/FilterDropdown';

export type DateRange = '1M' | '3M' | '6M' | '1Y' | '5Y' | 'ALL' | 'CUSTOM';

const DATE_RANGES: {label: string; value: DateRange}[] = [
  {label: '1 month', value: '1M'},
  {label: '3 months', value: '3M'},
  {label: '6 months', value: '6M'},
  {label: '1 year', value: '1Y'},
  {label: '5 years', value: '5Y'},
];

export type DateRangeState = {
  startDate: DateTime | null;
  endDate: DateTime | null;
};

type Props = {
  value: DateRangeState;
  onChange: (value: DateRangeState) => void;
};

const getPresetRange = (range: DateRange): DateRangeState => {
  const now = DateTime.now();
  switch (range) {
    case '1M':
      return {startDate: now.minus({months: 1}), endDate: null};
    case '3M':
      return {startDate: now.minus({months: 3}), endDate: null};
    case '6M':
      return {startDate: now.minus({months: 6}), endDate: null};
    case '1Y':
      return {startDate: now.minus({years: 1}), endDate: null};
    case '5Y':
      return {startDate: now.minus({years: 5}), endDate: null};
    case 'ALL':
    case 'CUSTOM':
      return {startDate: null, endDate: null};
  }
};

const getActivePreset = (value: DateRangeState): DateRange | null => {
  if (value.endDate) return null; // Custom end date means no preset
  if (!value.startDate) return 'ALL';

  const now = DateTime.now();
  const diffDays = now.diff(value.startDate, 'days').days;

  // Calendar months vary in length, so match against the full range of days
  // each preset can span (e.g., 3 months is 89-92 days)
  if (diffDays >= 27 && diffDays <= 32) return '1M';
  if (diffDays >= 88 && diffDays <= 93) return '3M';
  if (diffDays >= 179 && diffDays <= 185) return '6M';
  if (diffDays >= 364 && diffDays <= 367) return '1Y';
  if (diffDays >= 1824 && diffDays <= 1828) return '5Y';

  return null;
};

/**
 * Date-range picker as a compact filter pill (same pattern as the accounts
 * list's status/type dropdowns) so a row of chart controls fits on narrow
 * screens. Choosing "Custom" reveals start/end date inputs; the inputs render
 * with `basis-full` so inside a flex-wrap header they drop to their own row
 * instead of forcing a horizontal scroll.
 */
export const DateRangeFilter = ({value, onChange}: Props) => {
  const activePreset = getActivePreset(value);
  const [showCustom, setShowCustom] = React.useState(activePreset === null);

  const selection: string = showCustom
    ? 'CUSTOM'
    : activePreset === null
      ? 'CUSTOM'
      : activePreset === 'ALL'
        ? 'all'
        : activePreset;

  const handleSelect = (key: string) => {
    if (key === 'all') {
      setShowCustom(false);
      onChange(getPresetRange('ALL'));
    } else if (key === 'CUSTOM') {
      setShowCustom(true);
    } else {
      const preset = DATE_RANGES.find((r) => r.value === key)?.value;
      if (preset) {
        setShowCustom(false);
        onChange(getPresetRange(preset));
      }
    }
  };

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateStr = e.target.value;
    onChange({
      ...value,
      startDate: dateStr ? DateTime.fromISO(dateStr) : null,
    });
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateStr = e.target.value;
    onChange({
      ...value,
      endDate: dateStr ? DateTime.fromISO(dateStr) : null,
    });
  };

  return (
    <>
      <FilterDropdown
        label="Range"
        allLabel="All time"
        options={[
          ...DATE_RANGES.map(({label, value: preset}) => ({
            key: preset,
            label,
          })),
          {key: 'CUSTOM', label: 'Custom…'},
        ]}
        value={selection}
        onChange={handleSelect}
      />
      {showCustom && (
        <div className="flex basis-full items-center gap-2">
          <input
            type="date"
            value={value.startDate?.toISODate() ?? ''}
            onChange={handleStartChange}
            className="border-border bg-background text-foreground rounded border px-2 py-1 text-sm"
          />
          <span className="text-muted-foreground text-sm">to</span>
          <input
            type="date"
            value={value.endDate?.toISODate() ?? ''}
            onChange={handleEndChange}
            className="border-border bg-background text-foreground rounded border px-2 py-1 text-sm"
          />
        </div>
      )}
    </>
  );
};

export const useDateRangeFilter = (initialRange: DateRange = 'ALL') => {
  const [dateRange, setDateRange] = React.useState<DateRangeState>(() =>
    getPresetRange(initialRange),
  );

  const filterByDateRange = <T extends {date: string}>(items: T[]): T[] => {
    return items.filter((item) => {
      const itemDate = DateTime.fromISO(item.date);
      if (dateRange.startDate && itemDate < dateRange.startDate) return false;
      if (dateRange.endDate && itemDate > dateRange.endDate) return false;
      return true;
    });
  };

  return {dateRange, setDateRange, filterByDateRange};
};
